/* eslint-disable max-lines */
import {
  DocumentPayload,
  useCopyDocument
} from '@utils/api/documents/use-post-copy-document.tsx';
import toast from 'react-hot-toast';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useMemo } from 'react';
import cn from 'classnames';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import Input from '@components/atoms/ui-kit/input.tsx';
import { WorkspaceRow } from '@components/main-workspace/workspace.tsx';
import { BaseError } from '@utils/types/shared/base';
import { AxiosError } from 'axios';
import { ChevronDown, AlertTriangle, Info } from 'lucide-react';

export type CopyType = {
  id: number;
  title: string;
};

type DocumentOptionsProps = {
  isEditModalOpen: boolean;
  setIsEditModalOpen: (value: boolean) => void;
  selectedCopyType?: CopyType;
  setSelectedCopyType?: (value: CopyType) => void;
  selectedFilename: string | undefined;
  setSelectedFilename: (value: string) => void;
  selectedSessionId: string;
  selectedDocumentId: string;
  selectedItem?: WorkspaceRow;
  selectedCopyId?: string | undefined;
  setSelectedItem: React.Dispatch<
    React.SetStateAction<WorkspaceRow | undefined>
  >;
  folderId: string | undefined;
};

export const DocumentOptions = ({
  selectedCopyType,
  setSelectedCopyType,
  isEditModalOpen,
  setIsEditModalOpen,
  selectedFilename,
  setSelectedFilename,
  selectedSessionId,
  selectedItem,
  setSelectedItem,
  selectedCopyId,
  folderId
}: DocumentOptionsProps) => {
  const { t } = useTranslation();
  const { getColour, getContrastTextColor } = useThemeContext();
  const { mutate: copyDocumentMutation } = useCopyDocument();

  const activePillStyle = {
    backgroundColor: getColour('activeText'),
    borderColor: getColour('activeText'),
    color: getContrastTextColor(getColour('activeText'))
  };
  const inactivePillStyle = {
    backgroundColor: getColour('secondLayer'),
    borderColor: getColour('hairline'),
    color: getColour('normalText')
  };

  const [openWebhook, setOpenWebhook] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Internal state for copy type when parent doesn't manage it
  const [internalCopyType, setInternalCopyType] = useState<
    CopyType | undefined
  >(selectedCopyType);

  // Map from key to id for lookup (used to resolve selectedCopyId)
  const copyTypeKeyToId: { [key: string]: number } = {
    'original-transcript-editor': 1,
    'modified-transcript-editor': 2,
    'original-caption': 3,
    'modified-caption': 4
  };

  // copyType definitions (use t for titles)
  const copyType: CopyType[] = [
    { id: 1, title: t('original-transcript-editor') },
    { id: 2, title: t('modified-transcript-editor') },
    { id: 3, title: t('original-caption') },
    { id: 4, title: t('modified-caption') }
  ];

  // Normalize selectedCopyId into canonical key like "modified-transcript-editor"
  const normalizeSelectedCopyId = (id?: string) => {
    if (!id) return undefined;
    // Lowercase, replace whitespace with hyphens, replace underscores, remove unexpected chars
    const lowered = id.toLowerCase();
    const replaced = lowered.replace(/\s+/g, '-').replace(/_+/g, '-');
    const cleaned = replaced.replace(/[^a-z0-9-]/g, '');
    // collapse multiple hyphens
    return cleaned.replace(/-+/g, '-').trim();
  };

  // Try to resolve selectedCopyId to a CopyType by normalized key, else fuzzy match
  const resolveCopyTypeFromId = (rawId?: string): CopyType | undefined => {
    const normalized = normalizeSelectedCopyId(rawId);
    if (!normalized) return undefined;
    // direct map
    const targetId = copyTypeKeyToId[normalized];
    if (targetId) {
      const ct = copyType.find(c => c.id === targetId);
      if (ct) return ct;
    }
    // fuzzy fallback: check words
    const words = normalized.split('-');
    // prefer entries that include both 'original'/'modified' and 'caption'/'transcript'
    const fuzzy = copyType.find(ct => {
      const key =
        Object.keys(copyTypeKeyToId).find(k => copyTypeKeyToId[k] === ct.id) ??
        '';
      const keyWords = key.split('-');
      const matches = words.every(
        w => keyWords.includes(w) || keyWords.some(kw => kw.includes(w))
      );
      return matches;
    });
    if (fuzzy) return fuzzy;
    // last resort: check includes
    const includeMatch = copyType.find(ct => {
      const key =
        Object.keys(copyTypeKeyToId).find(k => copyTypeKeyToId[k] === ct.id) ??
        '';
      return key.includes(normalized) || normalized.includes(key);
    });
    return includeMatch;
  };

  // ACTIVE copy type resolution order:
  // 1. selectedCopyType prop (preferred)
  // 2. selectedCopyId prop mapped to copyType (if selectedCopyType is undefined)
  // 3. internalCopyType (fallback)
  const activeCopyType = useMemo<CopyType | undefined>(() => {
    if (selectedCopyType) return selectedCopyType;
    if (selectedCopyId) {
      const resolved = resolveCopyTypeFromId(selectedCopyId);
      if (resolved) return resolved;
    }
    return internalCopyType;
  }, [selectedCopyType, selectedCopyId, internalCopyType]);

  // Keep internal copy type in sync if selectedCopyId changes and parent is not managing the setter.
  useEffect(() => {
    if (!selectedCopyId) return;
    if (setSelectedCopyType) return; // parent manages it; no need to set internal
    const selectedOption = resolveCopyTypeFromId(selectedCopyId);
    if (!selectedOption) return;
    if (internalCopyType?.id !== selectedOption.id) {
      setInternalCopyType(selectedOption);
    }
  }, [selectedCopyId]);

  const [settings, setSettings] = useState({
    max_line_length: 37,
    max_line_words: 12,
    max_segment_duration: 7000,
    max_segment_words: 24,
    max_segment_lines: 2,
    max_silence_between_words: 1500,
    min_segment_gap: 120,
    webhook_headers: '',
    webhook_method: 'POST',
    webhook_url: ''
  });

  useEffect(() => {
    if (!selectedCopyType) return;
    // Reset settings for non-caption types (id 1 and 2 are transcripts, 3 and 4 are captions)
    if (selectedCopyType.id !== 3 && selectedCopyType.id !== 4) {
      setSettings({
        max_line_length: 37,
        max_line_words: 12,
        max_segment_duration: 7000,
        max_segment_words: 24,
        max_segment_lines: 2,
        max_silence_between_words: 1500,
        min_segment_gap: 120,
        webhook_headers: '',
        webhook_method: 'POST',
        webhook_url: ''
      });
    }
  }, [selectedCopyType]);

  const normalize = (s?: string) => (s ?? '').trim();

  function buildCopyFilename(
    baseName: string,
    optionSlug: string,
    userInput?: string
  ) {
    const base = normalize(baseName) || 'Untitled';
    const typed = normalize(userInput);

    // If the user entered something different, use it as-is.
    if (typed && typed !== base) return typed;

    // Otherwise, append a clear suffix so it's obviously a copy.
    // Example result: "My file — original-caption.copy"
    return `${base} — ${optionSlug}.copy`;
  }

  /**
   * Transform a multiline/comma-separated headers text into an array of {name, value}.
   * Example accepted input lines:
   *  X-Header: value
   *  X-Other value
   *
   * If your API expects a different shape (string[] or object map) swap this implementation.
   */
  function toHeaderArray(
    headersText?: string
  ): { name: string; value: string }[] | undefined {
    const raw = normalize(headersText);
    if (!raw) return undefined;
    const parts = raw
      .split(/\r?\n|,/)
      .map(s => s.trim())
      .filter(Boolean);
    if (!parts.length) return undefined;

    return parts
      .map(part => {
        // support "Name: value" or "Name value"
        if (part.includes(':')) {
          const idx = part.indexOf(':');
          const name = part.slice(0, idx).trim();
          const value = part.slice(idx + 1).trim();
          return { name, value };
        }
        const split = part.split(/\s+/);
        const name = split.shift() ?? '';
        const value = split.join(' ').trim();
        return { name: name.trim(), value: value.trim() };
      })
      .filter(h => h.name);
  }

  useEffect(() => {
    if (isEditModalOpen) {
      setSelectedFilename(selectedItem?.name ?? '');
    }
  }, [isEditModalOpen, selectedItem, setSelectedFilename]);

  const handleSettingChange = (name: string, value: string | number) => {
    const minMaxValues: { [key: string]: { min: number; max: number } } = {
      max_line_length: { min: 1, max: 200 },
      max_line_words: { min: 1, max: 100 },
      max_segment_duration: { min: 0, max: 200000 },
      max_segment_lines: { min: 1, max: 100 },
      max_segment_words: { min: 1, max: 100 },
      max_silence_between_words: { min: 0, max: 20000 },
      min_segment_gap: { min: 1, max: 100 }
    };

    if (typeof value === 'number' && name in minMaxValues) {
      const newValue = parseInt(value.toString(), 10);
      if (newValue < minMaxValues[name].min) {
        toast.error(`${t('minimum-value')} ${minMaxValues[name].min}`);
      } else if (newValue > minMaxValues[name].max) {
        toast.error(`${t('maximum-value')} ${minMaxValues[name].max}`);
      } else {
        setSettings(prev => ({ ...prev, [name]: newValue }));
      }
    } else {
      setSettings(prev => ({ ...prev, [name]: value }));
    }
  };

  /**
   * Attempt to call the copy mutation. Returns a Promise so callers can await & finally.
   */
  const handleOptionClick = async (option: string): Promise<void> => {
    if (!activeCopyType) throw new Error('copy-type-missing');
    // Session ID validation is already done in handleCopyAction
    if (!selectedSessionId) {
      throw new Error('session-id-missing');
    }
    if (
      (activeCopyType.id === 2 || activeCopyType.id === 4) &&
      !selectedItem?.id
    ) {
      throw new Error('document-id-missing');
    }

    const baseName = selectedItem?.name || '';
    const finalFilename = buildCopyFilename(baseName, option, selectedFilename);

    let payload: DocumentPayload;

    switch (activeCopyType.id) {
      case 1: // Original transcript
        payload = {
          document_type: 'document',
          filename: finalFilename,
          folder_id: folderId ?? null
        } as DocumentPayload;
        break;
      case 2: // Modified transcript
        if (!selectedItem?.id) throw new Error('Selected item ID is missing');
        payload = {
          document_type: 'document',
          filename: finalFilename,
          folder_id: folderId ?? null,
          document_id: selectedItem.id
        } as DocumentPayload;
        break;
      case 3: // Original caption
        payload = {
          document_type: 'caption',
          filename: finalFilename,
          folder_id: folderId ?? null,
          max_line_length: settings.max_line_length,
          max_line_words: settings.max_line_words,
          max_segment_duration: settings.max_segment_duration,
          max_segment_lines: settings.max_segment_lines,
          max_segment_words: settings.max_segment_words,
          max_silence_between_words: settings.max_silence_between_words,
          min_segment_gap: settings.min_segment_gap,
          webhook_headers: toHeaderArray(settings.webhook_headers),
          webhook_method: settings.webhook_method as 'POST' | 'PUT',
          webhook_url: normalize(settings.webhook_url) || null
        } as DocumentPayload;
        break;
      case 4: // Modified caption
        if (!selectedItem?.id) throw new Error('Selected item ID is missing');
        payload = {
          document_type: 'caption',
          filename: finalFilename,
          folder_id: folderId ?? null,
          document_id: selectedItem.id,
          max_line_length: settings.max_line_length,
          max_line_words: settings.max_line_words,
          max_segment_duration: settings.max_segment_duration,
          max_segment_lines: settings.max_segment_lines,
          max_segment_words: settings.max_segment_words,
          max_silence_between_words: settings.max_silence_between_words,
          min_segment_gap: settings.min_segment_gap,
          webhook_headers: toHeaderArray(settings.webhook_headers),
          webhook_method: settings.webhook_method as 'POST' | 'PUT',
          webhook_url: normalize(settings.webhook_url) || null
        } as DocumentPayload;
        break;
      default:
        throw new Error('Invalid copy type selected');
    }

    // Call mutation and return a promise that resolves/rejects based on API callbacks.
    return new Promise<void>((resolve, reject) => {
      copyDocumentMutation(
        { sessionId: selectedSessionId, payload },
        {
          onSuccess: () => {
            toast.success(t('successfully-copied') + ` ${option}`);
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
            setSelectedFilename('');
            resolve();
          },
          onError: (error: AxiosError<BaseError>) => {
            // Toast is handled by the global mutation error handler in
            // react-query-instance.ts. Only reject so the caller's
            // try/finally runs and clears the loading state.
            reject(error);
          }
        }
      );
    });
  };

  const getOptionFromCopyType = (copyType: CopyType) => {
    switch (copyType.id) {
      case 1:
        return 'original-transcript-editor';
      case 2:
        return 'modified-transcript-editor';
      case 3:
        return 'original-caption';
      case 4:
        return 'modified-caption';
      default:
        return '';
    }
  };

  const handleCopyAction = async () => {
    if (!selectedItem) {
      toast.error('No item selected');
      return;
    }
    if (!activeCopyType) {
      toast.error('No copy type selected');
      return;
    }
    if (!selectedSessionId) {
      toast.error(
        t('session-not-found') || 'Session not found. Cannot copy document.'
      );
      return;
    }

    const option = getOptionFromCopyType(activeCopyType);
    if (!option) {
      toast.error('Invalid copy type');
      return;
    }

    setIsSubmitting(true);
    try {
      await handleOptionClick(option);
    } catch (err) {
      // Error already handled and displayed in handleOptionClick
      // No need to show duplicate toast here
    } finally {
      setIsSubmitting(false);
    }
  };

  const FORM_ID = 'document-copy-options-form';

  const isCaptionFlow = Boolean(
    selectedCopyId &&
      (selectedCopyId.toLowerCase().includes('caption') ||
        activeCopyType?.id === 3 ||
        activeCopyType?.id === 4)
  );

  return (
    <Modal
      isOpen={isEditModalOpen}
      onClose={() => {
        setIsEditModalOpen(false);
        setSelectedItem(undefined);
      }}
      size="medium"
      title={t('document-copy-options')}
      footerActions={[
        <Button
          key="cancel-button"
          type="button"
          onClick={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="copy-button"
          type="submit"
          form={FORM_ID}
          label={isSubmitting ? t('copying') : t('copy')}
          size="small"
          variant="primary"
          disabled={isSubmitting}
        />
      ]}
    >
      <form
        id={FORM_ID}
        className="w-full max-h-none overflow-visible py-2 grid gap-5"
        onSubmit={e => {
          e.preventDefault();
          if (!isSubmitting) handleCopyAction();
        }}
      >
        <section className="rounded-2xl border border-hairline shadow-card bg-app-soft p-4 sm:p-5">
          <Input
            id="filename"
            label={t('filename')}
            name="filename"
            value={selectedFilename}
            onChange={e => setSelectedFilename(e.target.value)}
            type="text"
            autoComplete="off"
            className="w-full"
            helperText={t('leave-blank-or-unchanged-to-auto-append-copy')}
          />
        </section>

        {isCaptionFlow && (
          <section className="rounded-2xl border border-hairline shadow-card bg-app-soft p-4 sm:p-5 space-y-4">
            <div className="grid gap-2">
              <p
                className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: getColour('inactiveText') }}
              >
                {t('caption-settings')}
              </p>
              <div
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('industry-standard-values-subtitle-one')}
              </div>
              <div
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('industry-standard-values-subtitle-two')}
              </div>
              <div
                className="text-xs flex items-start gap-2"
                style={{ color: getColour('activeText') }}
              >
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                {t('ignored-setting-during-subtitle')}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="number"
                label={t('maximum-line-length')}
                value={settings.max_line_length}
                onChange={e =>
                  handleSettingChange(
                    'max_line_length',
                    parseInt(e.target.value)
                  )
                }
                name="max_line_length"
                id="max_line_length"
                autoComplete="off"
                className="w-full"
              />
              <Input
                type="number"
                label={t('max-words-line')}
                value={settings.max_line_words}
                onChange={e =>
                  handleSettingChange(
                    'max_line_words',
                    parseInt(e.target.value)
                  )
                }
                name="max_line_words"
                id="max_line_words"
                autoComplete="off"
                className="w-full"
              />
              <Input
                type="number"
                label={t('maximum-lines-segment')}
                value={settings.max_segment_lines}
                onChange={e =>
                  handleSettingChange(
                    'max_segment_lines',
                    parseInt(e.target.value)
                  )
                }
                name="max_segment_lines"
                id="max_segment_lines"
                autoComplete="off"
                className="w-full"
              />
              <Input
                type="number"
                label={t('maximum-segment-duration')}
                value={settings.max_segment_duration}
                onChange={e =>
                  handleSettingChange(
                    'max_segment_duration',
                    parseInt(e.target.value)
                  )
                }
                name="max_segment_duration"
                id="max_segment_duration"
                autoComplete="off"
                className="w-full"
              />
              <Input
                type="number"
                label={t('maximum-segment-words')}
                value={settings.max_segment_words}
                onChange={e =>
                  handleSettingChange(
                    'max_segment_words',
                    parseInt(e.target.value)
                  )
                }
                name="max_segment_words"
                id="max_segment_words"
                autoComplete="off"
                className="w-full"
              />
              <Input
                type="number"
                label={t('maximum-silence-words')}
                value={settings.max_silence_between_words}
                onChange={e =>
                  handleSettingChange(
                    'max_silence_between_words',
                    parseInt(e.target.value)
                  )
                }
                name="max_silence_between_words"
                id="max_silence_between_words"
                autoComplete="off"
                className="w-full"
              />
              <Input
                type="number"
                label={t('minimum-segment-gap')}
                value={settings.min_segment_gap}
                onChange={e =>
                  handleSettingChange(
                    'min_segment_gap',
                    parseInt(e.target.value)
                  )
                }
                name="min_segment_gap"
                id="min_segment_gap"
                autoComplete="off"
                className="w-full"
              />
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-hairline shadow-card bg-app-soft transition-colors">
          {/* Header */}
          <button
            type="button"
            onClick={() => setOpenWebhook(v => !v)}
            aria-expanded={openWebhook}
            aria-controls="webhook-panel"
            className={cn(
              'w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3',
              'text-sm font-medium rounded-2xl transition-colors',
              'hover:bg-app'
            )}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="truncate">{t('webhooks')}</span>
            </span>

            <span className="flex items-center gap-3">
              {!!settings.webhook_url && (
                <span
                  className="hidden sm:inline-flex items-center rounded-full px-2 py-0.5 text-[11px]
                         bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300
                         border border-emerald-200/60 dark:border-emerald-500/30"
                >
                  {t('configured')}
                </span>
              )}
              <span className="text-xs ">
                {openWebhook ? t('hide') : t('configure')}
              </span>
              <ChevronDown
                className={cn(
                  'w-4 h-4 transition-transform duration-300',
                  openWebhook && 'rotate-180'
                )}
                aria-hidden="true"
              />
            </span>
          </button>

          <div
            id="webhook-panel"
            className={cn(
              'grid transition-all duration-300 ease-out',
              openWebhook ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            <div className="overflow-hidden">
              <div className="px-4 sm:px-5 py-5 space-y-5 border-t border-hairline">
                <p className="text-sm">{t('webhook-settings-description')}</p>

                <Input
                  type="text"
                  name="webhook_url"
                  label={t('webhook-url')}
                  placeholder="https://example.com/webhook"
                  id="webhook_url"
                  autoComplete="off"
                  onChange={e =>
                    handleSettingChange('webhook_url', e.target.value)
                  }
                  className="w-full"
                />

                <fieldset className="w-full">
                  <legend
                    className="mb-2 text-xs font-semibold uppercase tracking-wide sm:mb-3"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('webhook-method')}
                  </legend>

                  <div
                    role="group"
                    aria-label={t('webhook-method')}
                    className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:min-w-[280px]"
                  >
                    {['POST', 'PUT'].map(m => {
                      const active = String(settings.webhook_method) === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          aria-pressed={active}
                          onClick={() =>
                            handleSettingChange('webhook_method', m)
                          }
                          className="w-full px-4 py-2 text-sm font-semibold rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                          style={active ? activePillStyle : inactivePillStyle}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                <div className="grid">
                  <Input
                    multiline
                    label={t('webhook-headers')}
                    name="webhook_headers"
                    id="webhook_headers"
                    placeholder="X-header: value"
                    type="text"
                    autoComplete="off"
                    onChange={e =>
                      handleSettingChange('webhook_headers', e.target.value)
                    }
                    className="w-full font-mono"
                    wrapperClass="w-full"
                  />
                </div>

                {/* Warning */}
                <div className="text-sm text-amber-500 dark:text-amber-300/90 flex items-start gap-2">
                  <AlertTriangle className="w-6 h-6" aria-hidden="true" />
                  <span className="pt-0.5">{t('webhook-warning')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </form>
    </Modal>
  );
};
